import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type { CallContact, CallContactInput } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import AssetField from "./AssetField";
import TimezoneSelect from "./TimezoneSelect";
import { ItemFormSurface, type Dismiss } from "./EditDrawer";
import { describeError, useToast } from "./toast";
import { Trans, useTranslation } from "react-i18next";
import { errorText } from "../utils/apiError";
import { useFormat } from "../i18n/format";
import { translateKey, type Translate } from "../i18n/translate";

/**
 * Add or edit ONE family contact for ONE resident (VC-11).
 *
 * Three things here are load-bearing rather than cosmetic:
 *
 *  1. **Consent gates the two direction flags, in the UI, structurally.** Not a
 *     warning next to an enabled checkbox - the checkboxes are disabled and the
 *     submitted payload carries `false` until a consent record exists. A family
 *     contact that reaches a TV without a recorded basis is the finding an
 *     inspection writes down, and "staff were told to tick the box" is not a
 *     control. Saving without consent is still allowed: the row is kept as an
 *     inactive draft so the daughter's number does not have to be re-typed
 *     later, which is exactly what makes staff willing to record consent
 *     properly instead of routing around us.
 *
 *  2. **Consent-by-proxy is first-class.** Swedish senior living means a
 *     meaningful share of residents have a god man or förvaltare, and "the
 *     resident consented" is then simply false. `capacity` is a required choice
 *     the moment consent is recorded, so the record can never be ambiguous
 *     about *whose* consent it was.
 *
 *  3. **The consent timestamp is never re-stamped by an unrelated edit.**
 *     `consent` is only sent when the operator actually touched it; fixing a
 *     typo in a phone number must not move "granted at" to today, because that
 *     date is the answer to "when was this authorised".
 *
 * Scope and kind are not offered: this dialog only ever writes
 * `scope: "person" / kind: "family"`. A family row keyed to anything but the
 * resident would be inherited by the next occupant of that bed.
 */

/** What staff actually type. Free text is still allowed - this is a shortcut. */
const RELATION_SUGGESTIONS = [
  "dotter",
  "son",
  "make",
  "maka",
  "sambo",
  "syskon",
  "barnbarn",
  "god man",
  "förvaltare",
  "vän",
];

/**
 * Four coarse buckets, not free text: "who may consent" is a legal question
 * with a small answer set. The value is the Swedish term; the label is translated.
 */
export const CONSENT_CAPACITIES: { value: string; labelKey: string }[] = [
  { value: "resident", labelKey: "resident" },
  { value: "god man", labelKey: "godMan" },
  { value: "förvaltare", labelKey: "forvaltare" },
  { value: "anhörig", labelKey: "anhorig" },
];

/** The display label for a stored capacity; an unknown value shows as stored. */
export function consentCapacityLabel(
  t: Translate,
  capacity: string | null | undefined
): string | null {
  if (!capacity) return null;
  const option = CONSENT_CAPACITIES.find((o) => o.value === capacity);
  return option
    ? translateKey(t, `care:familyContactDialog.capacity.${option.labelKey}`)
    : capacity;
}

/** E.164: leading +, no leading zero, 7-15 digits total. */
const E164 = /^\+[1-9]\d{6,14}$/;

export function isValidE164(v: string): boolean {
  return E164.test(v.trim());
}

/**
 * Deliberately loose: "has an @, something either side, no spaces". The address
 * is not a security boundary here - the portal only ever mails a single-use
 * link to it - so a strict RFC pattern would reject real addresses for nothing.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_PLACEHOLDER = "anna@example.com";

export function isValidEmail(v: string): boolean {
  return EMAIL.test(v.trim());
}

interface Props {
  open: boolean;
  premiseId: string;
  /** The resident this contact belongs to (PhiRecord id). */
  personRef: string;
  /** Human label for the room, for the dialog subtitle. */
  roomLabel: string;
  /** Null = create. */
  contact: CallContact | null;
  /** Premise timezone, prefilled into quiet hours so it's rarely retyped. */
  defaultTz: string;
  onClose: () => void;
  /** Receives the finished success sentence, ready to toast. */
  onSaved: (message: string) => void;
}

export default function FamilyContactDialog({
  open,
  premiseId,
  personRef,
  roomLabel,
  contact,
  defaultTz,
  onClose,
  onSaved,
}: Props) {
  const toast = useToast();
  const { t } = useTranslation(["care", "common"]);
  const fmt = useFormat();
  const editing = !!contact;

  const [displayName, setDisplayName] = useState("");
  const [relation, setRelation] = useState("");
  const [avatarAssetId, setAvatarAssetId] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [callableFromRoom, setCallableFromRoom] = useState(true);
  const [canCallRoom, setCanCallRoom] = useState(true);
  const [quietFrom, setQuietFrom] = useState("");
  const [quietTo, setQuietTo] = useState("");
  const [quietTz, setQuietTz] = useState(defaultTz);

  // Consent. `consentDirty` is what keeps an unrelated edit from re-stamping
  // `grantedAt` - see (3) in the header.
  const [consentGiven, setConsentGiven] = useState(false);
  const [capacity, setCapacity] = useState("");
  const [note, setNote] = useState("");
  const [consentDirty, setConsentDirty] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The portal link, shown once after it is minted (PORTAL-2).
  const [invite, setInvite] = useState<{
    url: string;
    expiresAt: string;
    emailed: boolean;
  } | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // The CALL link, shown once after it is minted (VC3-5). A different
  // credential from the one above - see the section header in the form.
  const [callLink, setCallLink] = useState<{
    url: string;
    emailed: boolean;
    rotated: boolean;
  } | null>(null);
  const [callLinkBusy, setCallLinkBusy] = useState(false);
  const [callLinkError, setCallLinkError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    setConsentDirty(false);
    setDisplayName(contact?.displayName ?? "");
    setRelation(contact?.relation ?? "");
    setAvatarAssetId(contact?.avatarAssetId ?? "");
    // Endpoints are encrypted and never read back into a form; blank means
    // "leave what is stored alone".
    setPhone("");
    setEmail("");
    setInvite(null);
    setCallLink(null);
    setCallLinkError(null);
    setInviteError(null);
    setCallableFromRoom(contact ? contact.callableFromRoom : true);
    setCanCallRoom(contact ? contact.canCallRoom : true);
    setQuietFrom(contact?.quietHours?.from ?? "");
    setQuietTo(contact?.quietHours?.to ?? "");
    setQuietTz(contact?.quietHours?.tz ?? defaultTz);
    setConsentGiven(!!contact?.consent);
    setCapacity(contact?.consent?.capacity ?? "");
    setNote(contact?.consent?.note ?? "");
  }, [open, contact, defaultTz]);

  // Un-consented rows are inert, always. Derived rather than written back into
  // state, so that recording consent restores what the operator had already
  // chosen instead of silently un-ticking both directions on them.
  const effectiveCallable = consentGiven && callableFromRoom;
  const effectiveCanCall = consentGiven && canCallRoom;

  const phoneTouched = phone.trim().length > 0;
  const phoneBad = phoneTouched && !isValidE164(phone);
  // On create we need a number; on edit, blank keeps the stored one.
  const phoneMissing = !editing && !phoneTouched;
  const emailTouched = email.trim().length > 0;
  const emailBad = emailTouched && !isValidEmail(email);
  const quietPartial =
    [quietFrom, quietTo].filter((v) => v.trim()).length === 1;
  const consentIncomplete = consentGiven && !capacity;

  // What is already stored, as masks - the values themselves are encrypted and
  // only ever leave the envelope through the audited reveal path.
  const storedPhone = contact?.endpointHints?.phone ?? null;
  const storedEmail = contact?.endpointHints?.email ?? null;

  /**
   * The server replaces the WHOLE endpoint envelope on every write - it cannot
   * merge, because it never reads the old values back into this form. So
   * touching one of the two fields while the other is stored but left blank
   * would quietly erase the other one. That is stated, loudly, instead of being
   * discovered later by a relative who can no longer sign in.
   */
  const endpointsTouched = phoneTouched || emailTouched;
  const losingPhone =
    editing && endpointsTouched && !phoneTouched && !!storedPhone;
  const losingEmail =
    editing && endpointsTouched && !emailTouched && !!storedEmail;

  const valid =
    !!displayName.trim() &&
    !phoneBad &&
    !phoneMissing &&
    !emailBad &&
    !quietPartial &&
    !consentIncomplete;

  /** Withdrawing consent on a live row is a real act; say what it does. */
  const withdrawing = editing && !!contact?.consent && !consentGiven;

  const consentRecord = useMemo(() => {
    if (!contact?.consent) return null;
    const c = contact.consent;
    const when = new Date(c.grantedAt);
    const label =
      consentCapacityLabel(t, c.capacity) ??
      t("familyContactDialog.capacity.unspecified");
    return t("familyContactDialog.consentRecord", {
      by: c.grantedBy,
      date: Number.isNaN(when.getTime()) ? c.grantedAt : fmt.date(when),
      capacity: label,
    });
  }, [contact, t, fmt]);

  const touchConsent =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setConsentDirty(true);
      setter(v);
    };

  async function submit(dismiss: Dismiss) {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);

    const quietHours =
      quietFrom.trim() && quietTo.trim()
        ? {
            from: quietFrom.trim(),
            to: quietTo.trim(),
            tz: quietTz.trim() || defaultTz,
          }
        : null;
    const consent = consentGiven
      ? { capacity: capacity || null, note: note.trim() || null }
      : null;
    const common = {
      displayName: displayName.trim(),
      relation: relation.trim() || null,
      avatarAssetId: avatarAssetId.trim() || null,
      // Consent gates both directions on the wire, not only in the checkboxes.
      callableFromRoom: effectiveCallable,
      canCallRoom: effectiveCanCall,
      quietHours,
    };

    // The complete envelope, always - see the note on `losingPhone` above.
    const endpoints = {
      ...(phoneTouched ? { phoneE164: phone.trim() } : {}),
      ...(emailTouched ? { email: email.trim().toLowerCase() } : {}),
    };

    try {
      if (contact) {
        await foyerApi.updateCallContact(premiseId, contact.id, {
          ...common,
          ...(endpointsTouched ? { endpoints } : {}),
          // Only when the operator actually touched it - otherwise a typo fix
          // would re-date the consent record.
          ...(consentDirty ? { consent } : {}),
        });
        const message = withdrawing
          ? t("familyContactDialog.withdrawn", { name: common.displayName })
          : t("familyContactDialog.updated", { name: common.displayName });
        // Slide out first, then let the list react and close.
        dismiss(() => {
          onSaved(message);
          onClose();
        });
      } else {
        const input: CallContactInput = {
          scope: "person",
          personRef,
          kind: "family",
          endpoints,
          autoAnswer: false,
          consent,
          ...common,
        };
        await foyerApi.createCallContact(premiseId, input);
        const message = consentGiven
          ? t("familyContactDialog.added", { name: common.displayName })
          : t("familyContactDialog.savedInactive", {
              name: common.displayName,
            });
        dismiss(() => {
          onSaved(message);
          onClose();
        });
      }
    } catch (err) {
      setError(errorText(err, t));
      toast.error(
        describeError(t, "saveContact", err, { name: common.displayName })
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * Mint a portal link for this contact. Staff get the plaintext link back ONCE
   * - it is also mailed when the contact has an email and a mail provider is
   * configured,
   * and `emailed` says which of the two actually happened, so nobody assumes a
   * delivery that never left the building.
   */
  async function sendInvite() {
    if (!contact || inviteBusy) return;
    setInviteBusy(true);
    setInviteError(null);
    try {
      setInvite(await foyerApi.sendFamilyPortalInvite(premiseId, contact.id));
      toast.success(
        t("familyContactDialog.portalInviteCreated", {
          name: contact.displayName,
        })
      );
    } catch (err) {
      setInviteError(errorText(err, t));
      toast.error(
        describeError(t, "createPortalInvite", err, {
          name: contact.displayName,
        })
      );
    } finally {
      setInviteBusy(false);
    }
  }

  /**
   * Mint the CALL link (VC3-5) - the one that lets this relative answer a call.
   * Same "shown once, mailed when we can" contract as the portal link, with one
   * extra thing to say: minting it retires the previous one, so a link already
   * sent to the family stops working. `rotated` says whether that just happened.
   */
  async function sendCallLink() {
    if (!contact || callLinkBusy) return;
    setCallLinkBusy(true);
    setCallLinkError(null);
    try {
      setCallLink(await foyerApi.sendFamilyCallLink(premiseId, contact.id));
      toast.success(
        t("familyContactDialog.callLinkCreated", { name: contact.displayName })
      );
    } catch (err) {
      setCallLinkError(errorText(err, t));
      toast.error(
        describeError(t, "createCallLink", err, {
          name: contact.displayName,
        })
      );
    } finally {
      setCallLinkBusy(false);
    }
  }

  const lost =
    losingPhone && losingEmail
      ? t("familyContactDialog.lostBoth", {
          number: t("familyContactDialog.lostNumber", { phone: storedPhone }),
          email: t("familyContactDialog.lostEmail", { email: storedEmail }),
        })
      : losingPhone
        ? t("familyContactDialog.lostNumber", { phone: storedPhone })
        : t("familyContactDialog.lostEmail", { email: storedEmail });

  return (
    // Adding is a modal; editing an existing contact slides in from the right
    // (stacked on the Family drawer it was opened from).
    <ItemFormSurface
      mode={editing ? "edit" : "create"}
      open={open}
      onClose={onClose}
      busy={busy}
      maxWidth="sm"
      title={
        editing
          ? t("familyContactDialog.titleEdit")
          : t("familyContactDialog.titleAdd")
      }
      subtitle={roomLabel}
      data-testid="family-contact-dialog"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={busy}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void submit(dismiss)}
            disabled={!valid || busy}
            data-testid="family-submit"
          >
            {busy
              ? t("common:states.saving")
              : editing
                ? t("common:buttons.save")
                : consentGiven
                  ? t("familyContactDialog.addContact")
                  : t("familyContactDialog.saveInactive")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2.5} sx={{ mt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          size="small"
          label={t("familyContactDialog.displayName")}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          fullWidth
          autoFocus
          required
          helperText={t("familyContactDialog.displayNameHelp")}
          inputProps={{ "data-testid": "family-display-name" }}
        />

        <Autocomplete
          freeSolo
          options={RELATION_SUGGESTIONS}
          value={relation}
          onInputChange={(_, v) => setRelation(v)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t("familyContactDialog.relation")}
              placeholder={t("familyContactDialog.relationPlaceholder")}
              helperText={t("familyContactDialog.relationHelp")}
              inputProps={{
                ...params.inputProps,
                "data-testid": "family-relation",
              }}
            />
          )}
        />

        <Box>
          <AssetField
            label={t("familyContactDialog.photo")}
            value={avatarAssetId}
            onChange={setAvatarAssetId}
            testId="family-avatar"
            preview
          />
        </Box>

        <TextField
          size="small"
          label={
            editing
              ? t("familyContactDialog.phoneKeep")
              : t("familyContactDialog.phoneNew")
          }
          placeholder="+4671234567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          fullWidth
          required={!editing}
          error={phoneBad}
          helperText={
            phoneBad
              ? t("familyContactDialog.phoneInvalid")
              : editing
                ? storedPhone
                  ? t("familyContactDialog.phoneEditHelpStored", {
                      phone: storedPhone,
                    })
                  : t("familyContactDialog.phoneEditHelp")
                : t("familyContactDialog.phoneNewHelp")
          }
          inputProps={{ "data-testid": "family-phone" }}
        />

        <TextField
          size="small"
          label={
            editing
              ? t("familyContactDialog.emailKeep")
              : t("familyContactDialog.emailOptional")
          }
          placeholder={EMAIL_PLACEHOLDER}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          error={emailBad}
          helperText={
            emailBad
              ? t("familyContactDialog.emailInvalid")
              : storedEmail
                ? t("familyContactDialog.emailStoredHelp", {
                    email: storedEmail,
                  })
                : editing
                  ? t("familyContactDialog.emailEditHelp")
                  : t("familyContactDialog.emailNewHelp")
          }
          inputProps={{ "data-testid": "family-email" }}
        />

        {(losingPhone || losingEmail) && (
          <Alert severity="warning" data-testid="family-endpoint-loss">
            {t("familyContactDialog.endpointLoss", { lost })}
          </Alert>
        )}

        {editing && (
          <>
            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                {t("familyContactDialog.linksSection")}
              </Typography>
            </Divider>

            <Typography variant="caption" color="text.secondary">
              <Trans
                t={t}
                i18nKey="familyContactDialog.linksIntro"
                components={{ strong: <strong /> }}
              />
            </Typography>

            {/* VC3-5 - the credential that lets them pick up. */}
            <Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => void sendCallLink()}
                disabled={callLinkBusy}
                data-testid="family-call-link"
              >
                {callLinkBusy
                  ? t("familyContactDialog.creatingLink")
                  : t("familyContactDialog.sendCallLink")}
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                {t("familyContactDialog.callLinkHelp")}{" "}
                {!consentGiven && t("familyContactDialog.callLinkNoConsent")}
              </Typography>
              {callLinkError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {callLinkError}
                </Alert>
              )}
              {callLink && (
                <Alert
                  severity={callLink.rotated ? "warning" : "success"}
                  sx={{ mt: 1, wordBreak: "break-all" }}
                  data-testid="family-call-link-result"
                >
                  {callLink.rotated && t("familyContactDialog.callLinkRotated")}{" "}
                  {callLink.emailed
                    ? t("familyContactDialog.callLinkMailed")
                    : t("familyContactDialog.linkNotMailed")}
                  <br />
                  <strong>{callLink.url}</strong>
                </Alert>
              )}
            </Box>

            {/* PORTAL-2 - the durable, money-spending surface. */}
            <Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => void sendInvite()}
                disabled={inviteBusy}
                data-testid="family-portal-invite"
              >
                {inviteBusy
                  ? t("familyContactDialog.creatingLink")
                  : t("familyContactDialog.sendPortalLink")}
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                {t("familyContactDialog.portalHelp")}
              </Typography>
              {inviteError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {inviteError}
                </Alert>
              )}
              {invite && (
                <Alert
                  severity="success"
                  sx={{ mt: 1, wordBreak: "break-all" }}
                  data-testid="family-portal-invite-result"
                >
                  {invite.emailed
                    ? t("familyContactDialog.portalMailed")
                    : t("familyContactDialog.linkNotMailed")}
                  <br />
                  <strong>{invite.url}</strong>
                </Alert>
              )}
            </Box>
          </>
        )}

        <Divider textAlign="left">
          <Typography variant="caption" color="text.secondary">
            {t("familyContactDialog.consent")}
          </Typography>
        </Divider>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={consentGiven}
                onChange={(e) => {
                  setConsentDirty(true);
                  setConsentGiven(e.target.checked);
                }}
                data-testid="family-consent-given"
              />
            }
            label={t("familyContactDialog.consentGiven")}
          />
          {consentRecord && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              data-testid="family-consent-record"
            >
              {consentRecord}
            </Typography>
          )}
        </Box>

        {consentGiven ? (
          <Stack spacing={2}>
            <FormControl
              size="small"
              fullWidth
              required
              error={consentIncomplete}
            >
              <InputLabel id="family-capacity-label">
                {t("familyContactDialog.capacityLabel")}
              </InputLabel>
              <Select
                labelId="family-capacity-label"
                label={t("familyContactDialog.capacityLabel")}
                value={capacity}
                onChange={(e) => touchConsent(setCapacity)(e.target.value)}
                data-testid="family-consent-capacity"
              >
                {CONSENT_CAPACITIES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {consentCapacityLabel(t, o.value)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {consentIncomplete
                  ? t("familyContactDialog.capacityRequired")
                  : t("familyContactDialog.capacityHelp")}
              </FormHelperText>
            </FormControl>
            <TextField
              size="small"
              label={t("familyContactDialog.note")}
              placeholder={t("familyContactDialog.notePlaceholder")}
              value={note}
              onChange={(e) => touchConsent(setNote)(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              helperText={t("familyContactDialog.noteHelp")}
              inputProps={{ "data-testid": "family-consent-note" }}
            />
          </Stack>
        ) : (
          <Alert severity="warning" data-testid="family-consent-missing">
            {withdrawing
              ? t("familyContactDialog.withdrawing")
              : t("familyContactDialog.noConsent")}
          </Alert>
        )}

        <Divider textAlign="left">
          <Typography variant="caption" color="text.secondary">
            {t("familyContactDialog.whatTheyMayDo")}
          </Typography>
        </Divider>

        <Tooltip
          title={
            consentGiven ? "" : t("familyContactDialog.recordConsentFirst")
          }
        >
          <Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={effectiveCallable}
                  disabled={!consentGiven}
                  onChange={(e) => setCallableFromRoom(e.target.checked)}
                  inputProps={
                    {
                      "data-testid": "family-callable-from-room",
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label={t("familyContactDialog.residentCanCallContact")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={effectiveCanCall}
                  disabled={!consentGiven}
                  onChange={(e) => setCanCallRoom(e.target.checked)}
                  inputProps={
                    {
                      "data-testid": "family-can-call-room",
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label={t("familyContactDialog.theyCanCall")}
            />
          </Stack>
        </Tooltip>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            size="small"
            type="time"
            label={t("familyContactDialog.quietFrom")}
            value={quietFrom}
            onChange={(e) => setQuietFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            error={quietPartial}
            inputProps={{ "data-testid": "family-quiet-from" }}
          />
          <TextField
            size="small"
            type="time"
            label={t("familyContactDialog.quietTo")}
            value={quietTo}
            onChange={(e) => setQuietTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            error={quietPartial}
            inputProps={{ "data-testid": "family-quiet-to" }}
          />
          <TimezoneSelect
            value={quietTz}
            onChange={(tz) => setQuietTz(tz || defaultTz)}
            testId="family-quiet-tz"
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {quietPartial
            ? t("familyContactDialog.quietPartial")
            : t("familyContactDialog.quietHelp")}
        </Typography>
      </Stack>
    </ItemFormSurface>
  );
}
